import { useState, useCallback, useRef } from 'react';
import { supabase } from '@/lib/supabase';
import { usePregnancyStore } from '@/store/pregnancyStore';
import type { ProductLookupResponse, ProductScan, ProductType } from '../types';

export interface ProductSearchResult {
  barcode: string;
  name: string;
  brand: string;
  image_small_url: string;
}

// Muss mit dem SearchErrorType in supabase/functions/product-lookup/index.ts
// uebereinstimmen. 'rate_limited' = OFF/OBF hat 429 geantwortet, 'unavailable'
// = jeder andere Fehler (5xx, Netzwerk). Ein durch searchAbortRef.current
// ausgeloester Abbruch (schnelleres Tippen ueberholt die vorherige Suche)
// zaehlt NICHT dazu - der ist beabsichtigt und wird still ignoriert, siehe
// searchProducts unten.
export type SearchErrorType = 'rate_limited' | 'unavailable';

export interface ProductSearchOutcome {
  products: ProductSearchResult[];
  // Nur gesetzt, wenn OFF/OBF fehlgeschlagen ist. Lokale Treffer (products)
  // koennen trotzdem vorhanden sein - ein Fehler bei der externen Suche
  // bedeutet nicht, dass es gar keine Ergebnisse gibt.
  searchError?: SearchErrorType;
}

interface SearchResponse {
  products: ProductSearchResult[];
  total: number;
  searchError?: SearchErrorType;
}

interface UseProductScannerReturn {
  lookupProduct: (barcode: string, productType?: ProductType, saveToHistory?: boolean) => Promise<ProductLookupResponse | null>;
  searchProducts: (query: string, limit?: number, productType?: ProductType) => Promise<ProductSearchOutcome>;
  getScanHistory: (limit?: number, productType?: ProductType) => Promise<ProductScan[]>;
  toggleFavorite: (scanId: string, isFavorite: boolean) => Promise<boolean>;
  deleteScan: (scanId: string) => Promise<boolean>;
  isLoading: boolean;
  isSearching: boolean;
  error: string | null;
}

export function useProductScanner(): UseProductScannerReturn {
  const [isLoading, setIsLoading] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { currentPregnancy } = usePregnancyStore();
  const searchAbortRef = useRef<AbortController | null>(null);

  // Calculate current SSW
  const getCurrentSSW = useCallback(() => {
    if (!currentPregnancy?.expected_due_date) return null;

    const today = new Date();
    const dueDate = new Date(currentPregnancy.expected_due_date);
    const diffTime = dueDate.getTime() - today.getTime();
    const diffWeeks = Math.ceil(diffTime / (1000 * 60 * 60 * 24 * 7));
    const currentSSW = 40 - diffWeeks;

    return Math.max(1, Math.min(42, currentSSW));
  }, [currentPregnancy?.expected_due_date]);

  // Lookup product by barcode
  const lookupProduct = useCallback(async (barcode: string, productType: ProductType = 'food', saveToHistory: boolean = true): Promise<ProductLookupResponse | null> => {
    setIsLoading(true);
    setError(null);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Nicht angemeldet');
      }

      const response = await supabase.functions.invoke('product-lookup', {
        body: {
          barcode,
          productType,
          saveToHistory,
          currentSSW: getCurrentSSW(),
        },
      });

      if (response.error) {
        throw new Error(response.error.message || 'Fehler beim Abrufen des Produkts');
      }

      return response.data as ProductLookupResponse;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Ein Fehler ist aufgetreten';
      setError(message);
      console.error('Product lookup error:', err);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [getCurrentSSW]);

  // Search products by name
  const searchProducts = useCallback(async (query: string, limit = 5, productType: ProductType = 'food'): Promise<ProductSearchOutcome> => {
    // Cancel previous search if still running
    if (searchAbortRef.current) {
      searchAbortRef.current.abort();
    }

    const trimmedQuery = query.trim();
    if (trimmedQuery.length < 2) {
      return { products: [] };
    }

    setIsSearching(true);
    const abortController = new AbortController();
    searchAbortRef.current = abortController;

    // Ein Abbruch (neuere Eingabe hat diese Suche ueberholt) ist beabsichtigt
    // und kein Fehler - weder als searchError an die UI durchreichen noch
    // als echten Fehler loggen.
    const wasAborted = () => abortController.signal.aborted;

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        return { products: [] };
      }

      const response = await supabase.functions.invoke('product-lookup', {
        body: {
          searchQuery: trimmedQuery,
          productType,
          limit,
        },
        // Signal tatsaechlich an den Request durchreichen - vorher wurde nur
        // signal.aborted im Nachhinein geprueft, ohne dass der Abort den
        // Request selbst je gestoppt hat. Der lief bis dahin immer komplett
        // durch und zaehlte trotzdem gegen das OFF-Rate-Limit.
        signal: abortController.signal,
      });

      // Abort kann je nach Timing entweder response.error setzen oder den
      // Aufruf werfen lassen (dann greift die gleiche Pruefung im catch
      // unten) - deshalb hier VOR der Fehlerauswertung geprueft, damit ein
      // beabsichtigter Abbruch nicht als "Search error" geloggt wird.
      if (wasAborted()) {
        return { products: [] };
      }

      if (response.error) {
        console.error('Search error:', response.error);
        return { products: [] };
      }

      const data = response.data as SearchResponse;
      return { products: data.products || [], searchError: data.searchError };
    } catch (err) {
      if (wasAborted()) {
        return { products: [] };
      }
      console.error('Product search error:', err);
      return { products: [] };
    } finally {
      setIsSearching(false);
    }
  }, []);

  // Get scan history
  const getScanHistory = useCallback(async (limit = 50, productType?: ProductType): Promise<ProductScan[]> => {
    setIsLoading(true);
    setError(null);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('Nicht angemeldet');
      }

      // Fetch scans with product data
      let query = supabase
        .from('user_product_scans')
        .select(`
          *,
          products (
            barcode,
            name,
            brand,
            categories,
            image_url,
            image_small_url,
            ingredients_text,
            nutriscore_grade,
            nova_group,
            quantity
          )
        `)
        .eq('user_id', user.id)
        .order('scanned_at', { ascending: false })
        .limit(limit);

      // Filter by product type if specified
      if (productType) {
        query = query.eq('product_type', productType);
      }

      const { data: scans, error: fetchError } = await query;

      if (fetchError) {
        throw fetchError;
      }

      // Transform the data
      return (scans || []).map(scan => ({
        ...scan,
        product: scan.products || undefined,
        product_type: scan.product_type || 'food', // Default to food for old scans
      })) as ProductScan[];
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Fehler beim Laden der Historie';
      setError(message);
      console.error('Get scan history error:', err);
      return [];
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Toggle favorite status
  const toggleFavorite = useCallback(async (scanId: string, isFavorite: boolean): Promise<boolean> => {
    try {
      const { error: updateError } = await supabase
        .from('user_product_scans')
        .update({ is_favorite: isFavorite })
        .eq('id', scanId);

      if (updateError) {
        throw updateError;
      }

      return true;
    } catch (err) {
      console.error('Toggle favorite error:', err);
      return false;
    }
  }, []);

  // Delete scan
  const deleteScan = useCallback(async (scanId: string): Promise<boolean> => {
    try {
      const { error: deleteError } = await supabase
        .from('user_product_scans')
        .delete()
        .eq('id', scanId);

      if (deleteError) {
        throw deleteError;
      }

      return true;
    } catch (err) {
      console.error('Delete scan error:', err);
      return false;
    }
  }, []);

  return {
    lookupProduct,
    searchProducts,
    getScanHistory,
    toggleFavorite,
    deleteScan,
    isLoading,
    isSearching,
    error,
  };
}
